import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FloppyDisk, Lock, LockOpen } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import api, { formatApiError } from "../lib/api";
import { fmtDate, fmtDateKeepSuffix } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const VERDICT_OPTS = [["", "— outcome —"], ["positive", "Positive reference"], ["negative", "Not positive"], ["no_response", "No response"]];
const YN = [["", "—"], ["yes", "Yes"], ["no", "No"]];
const reviewToVerdict = (rev) => ({ "satisfactory": "positive", "not satisfactory": "negative", "no response": "no_response" }[(rev || "").toLowerCase()] || "");

const inputCls = "h-8 w-full rounded-md border border-stone-300 bg-white px-2 text-xs focus:border-primary/60 focus:outline-none";
const lockedCls = "h-8 w-full rounded-md border border-stone-200 bg-stone-100 px-2 text-xs text-stone-600";

function RefSlot({ ref_: r, onChange, testid, refDocs = [], me = "", uid, idx, isAdmin = false, onUnlock }) {
  const verdict = r.verdict ?? reviewToVerdict(r.review);
  const noteMatch = (r.note || "").match(/filed as (.+)$/);
  const refDocId = noteMatch ? (refDocs.find((d) => d.title === noteMatch[1]) || {}).id : null;
  const complete = verdict === "positive" && !!r.reviewed_on;
  const hasOutcome = !!(r.verdict ?? reviewToVerdict(r.review));
  const locked = !!r.locked;
  const allYes = r.q_kind === "yes" && r.q_recommend === "yes" && r.reference_says_positive === "yes" && r.q_not_fit === "no";
  const selCls = (v) => `${locked ? lockedCls : inputCls} ${v === "yes" ? "border-[#27AE60]/50 bg-[#27AE60]/5 font-semibold text-[#1e8449]" : v === "no" ? "border-[#C0392B]/40 bg-[#C0392B]/5 text-[#C0392B]" : ""}`;
  const unlock = async () => {
    const reason = window.prompt(`Unlock ${r.name || "this reference"}?\n\nThis is an emailed form response — the reference's own words. It stays locked unless there's a real reason (e.g. fixing a wrong match). Your reason is recorded.`, "");
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/employees/${uid}/references/${idx}/unlock`, { reason: reason.trim(), name: r.name || "" });
      toast.success("Unlocked — reason recorded");
      onUnlock && onUnlock();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className={`space-y-1.5 rounded-lg border p-2 ${complete ? "border-[#27AE60]/50 bg-[#27AE60]/10" : locked ? "border-[#6a3e97]/30 bg-[#6a3e97]/5" : "border-stone-200 bg-stone-50/60"}`} data-testid={testid}>
      {locked && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testid}-locked-banner`}>
          <span className="inline-flex items-center gap-1 rounded-full border border-[#6a3e97]/40 bg-[#6a3e97]/10 px-2 py-0.5 text-[10px] font-bold text-[#6a3e97]">
            <Lock size={11} weight="fill" /> Email form response — locked{r.submission_date ? ` · responded ${r.submission_date}` : ""}
          </span>
          <span className="text-[9px] text-stone-400">The reference's own answers can't be edited.</span>
          {isAdmin && (
            <button onClick={unlock} className="ml-auto inline-flex items-center gap-1 rounded-md border border-stone-300 bg-white px-2 py-1 text-[10px] font-semibold text-stone-500 hover:border-[#6a3e97]/50 hover:text-[#6a3e97]"
              data-testid={`${testid}-unlock`}><LockOpen size={11} /> Unlock (reason recorded)</button>
          )}
        </div>
      )}
      {!locked && r.unlock_history?.length > 0 && (
        <p className="text-[9px] italic text-[#8a5d14]" data-testid={`${testid}-unlock-history`}>
          Unlocked {r.unlock_history[r.unlock_history.length - 1].at?.slice(0, 10)} by {r.unlock_history[r.unlock_history.length - 1].by}: {r.unlock_history[r.unlock_history.length - 1].reason}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <input value={r.name} placeholder="Reference name" onChange={(e) => onChange({ ...r, name: e.target.value })} disabled={locked}
          className={`${locked ? lockedCls : inputCls} min-w-32 flex-1`} data-testid={`${testid}-name`} />
        <input value={r.contact || ""} placeholder="Phone / email" onChange={(e) => onChange({ ...r, contact: e.target.value })} disabled={locked}
          className={`${locked ? lockedCls : inputCls} min-w-32 flex-1`} data-testid={`${testid}-contact`} />
        <input value={r.type || ""} placeholder="Relationship" onChange={(e) => onChange({ ...r, type: e.target.value })} disabled={locked}
          className={`${locked ? lockedCls : inputCls} w-28`} data-testid={`${testid}-type`} />
        <input value={r.known_for || ""} placeholder="Known how long?" onChange={(e) => onChange({ ...r, known_for: e.target.value })} disabled={locked}
          className={`${locked ? lockedCls : inputCls} w-28`} data-testid={`${testid}-known-for`} />
        {!locked && (
          <label className="flex items-center gap-1 rounded-md border border-stone-300 bg-white px-2 py-1 text-[10px] font-semibold text-stone-600" data-testid={`${testid}-all-yes`}>
            <input type="checkbox" checked={allYes}
              onChange={(e) => onChange({ ...r, q_kind: e.target.checked ? "yes" : "", q_recommend: e.target.checked ? "yes" : "", reference_says_positive: e.target.checked ? "yes" : "", q_not_fit: e.target.checked ? "no" : "" })} />
            All yes
          </label>
        )}
        <button onClick={() => onChange({ ...r, archived: true, archived_reason: "Archived manually by office" })}
          title="Archive this reference (kept for the record, doesn't count)"
          className={`${locked ? "" : "ml-auto "}rounded-md border border-stone-300 bg-white px-2 py-1 text-[10px] font-semibold text-stone-400 hover:border-[#C0392B]/40 hover:text-[#C0392B]`}
          data-testid={`${testid}-archive`}>Archive</button>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <label className="flex items-center gap-1 text-[10px] text-stone-500">
          Kind &amp; good character?
          <select value={r.q_kind || ""} onChange={(e) => onChange({ ...r, q_kind: e.target.value })} disabled={locked} className={`${selCls(r.q_kind)} w-16`} data-testid={`${testid}-q-kind`}>
            {YN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1 text-[10px] text-stone-500">
          Recommends for caring for vulnerable people at home?
          <select value={r.q_recommend || ""} onChange={(e) => onChange({ ...r, q_recommend: e.target.value })} disabled={locked} className={`${selCls(r.q_recommend)} w-16`} data-testid={`${testid}-q-recommend`}>
            {YN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1 text-[10px] text-stone-500">
          Any reason to believe NOT a good fit?
          <select value={r.q_not_fit || ""} onChange={(e) => onChange({ ...r, q_not_fit: e.target.value })} disabled={locked} className={`${selCls(r.q_not_fit)} w-16`} data-testid={`${testid}-q-not-fit`}>
            {YN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        {locked ? (
          <>
            {r.note && (
              <div className="w-full rounded-md border border-stone-200 bg-stone-100 px-2 py-1.5 text-xs text-stone-600" data-testid={`${testid}-answers`}>
                <span className="mr-1 text-[9px] font-bold uppercase text-stone-400">Their answers:</span>{r.note}
              </div>
            )}
            {r.verbatim && (
              <div className="w-full whitespace-pre-wrap rounded-md border border-stone-200 bg-stone-100 px-2 py-1.5 text-xs italic text-stone-600" data-testid={`${testid}-verbatim`}>{r.verbatim}</div>
            )}
            <textarea value={r.office_note || ""} placeholder="Office notes / additional information (as much as you need)" rows={2}
              onChange={(e) => onChange({ ...r, office_note: e.target.value })}
              className="w-full rounded-md border border-stone-300 bg-white px-2 py-1.5 text-xs focus:border-primary/60 focus:outline-none"
              data-testid={`${testid}-office-note-input`} />
          </>
        ) : (
          <textarea value={r.note || ""} placeholder="Office notes / additional information (as much as you need)" rows={2}
            onChange={(e) => onChange({ ...r, note: e.target.value })}
            className="w-full rounded-md border border-stone-300 bg-white px-2 py-1.5 text-xs focus:border-primary/60 focus:outline-none"
            data-testid={`${testid}-note-input`} />
        )}
        {r.source === "employee application" && refDocId && (
          <a href={`${api.defaults.baseURL}/employee-file-docs/${refDocId}/file`} target="_blank" rel="noreferrer"
            className="text-[10px] font-semibold text-primary hover:underline" data-testid={`${testid}-view-sheet`}>
            employee's reference sheet ↗
          </a>
        )}
        {!locked && hasOutcome && (r.note || r.verbatim || r.office_note) && (
          <div className="w-full space-y-1 rounded-md border border-[#27AE60]/30 bg-white px-2 py-1.5" data-testid={`${testid}-full-details`}>
            {r.note && <p className="text-[11px] text-stone-700"><span className="mr-1 text-[9px] font-bold uppercase text-stone-400">Work habits:</span>{r.note}</p>}
            {r.verbatim && <p className="whitespace-pre-wrap text-[11px] italic text-stone-700">{r.verbatim}</p>}
            {r.office_note && <p className="text-[11px] text-stone-700"><span className="mr-1 text-[9px] font-bold uppercase text-stone-400">Interviewer's impression:</span>{(r.office_note || "").replace(/^Interviewer's impression:\s*/i, "")}</p>}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {r.source === "employee application" && !hasOutcome && (
          <span className="text-[9px] text-stone-500" data-testid={`${testid}-taken-by`}>
            From the employee's application — call not made yet
          </span>
        )}
        <label className="flex items-center gap-1 text-[10px] font-semibold text-stone-600">
          Asked directly — positive reference? <span className="font-normal text-stone-400">(their answer)</span>
          <select value={r.reference_says_positive || ""} onChange={(e) => onChange({ ...r, reference_says_positive: e.target.value })} disabled={locked}
            className={`${selCls(r.reference_says_positive)} w-16`} data-testid={`${testid}-ref-says-positive`}>
            {YN.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        {!locked && (
          <label className="flex items-center gap-1 text-[10px] font-semibold text-stone-600">
            Taken by:
            <input value={r.reviewed_by || ""} placeholder="Who took it (on the form)"
              onChange={(e) => onChange({ ...r, reviewed_by: e.target.value })}
              className={`${inputCls} w-32`} data-testid={`${testid}-taken-by-input`} />
          </label>
        )}
        <label className="flex items-center gap-1 text-[10px] font-semibold text-stone-600">
          Approved by:
          <input value={r.approved_by || ""} placeholder="Kelly Schmunk"
            onChange={(e) => onChange({ ...r, approved_by: e.target.value })}
            className={`${inputCls} w-32`} data-testid={`${testid}-approved-by`} />
        </label>
        <label className="flex items-center gap-1 text-[10px] font-semibold text-stone-600">
          Office mark:
          <select value={verdict} onChange={(e) => onChange({ ...r, verdict: e.target.value })}
            className={`${inputCls} w-40 ${verdict === "positive" ? "border-[#27AE60]/50 bg-[#27AE60]/5 font-bold text-[#1e8449]" : verdict === "negative" ? "border-[#C0392B]/40 bg-[#C0392B]/5 font-semibold text-[#C0392B]" : ""}`}
            data-testid={`${testid}-verdict`}>
            {VERDICT_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <input type="date" value={r.reviewed_on || ""} title="Date checked" onChange={(e) => onChange({ ...r, reviewed_on: e.target.value })}
          className={`${inputCls} w-32`} data-testid={`${testid}-checked-on`} />
      </div>
    </div>
  );
}

export default function HiringRegisterPage() {
  const [rows, setRows] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [offerEdit, setOfferEdit] = useState(null); // {aid, name, email, position, pay_rate, evv_bonus, differential, start_date, schedule_notes}
  const [offerBusy, setOfferBusy] = useState(false);
  const resendOffer = async (aid, name) => {
    setOfferBusy(true);
    try {
      await api.post(`/applicants/${aid}/offer/resend`);
      toast.success(`Offer email resent to ${name}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOfferBusy(false); }
  };
  const saveOffer = async () => {
    setOfferBusy(true);
    try {
      await api.put(`/applicants/${offerEdit.aid}/offer`, {
        position: offerEdit.position, pay_rate: offerEdit.pay_rate, evv_bonus: offerEdit.evv_bonus,
        differential: offerEdit.differential, start_date: offerEdit.start_date,
        schedule_notes: offerEdit.schedule_notes, resend: true });
      toast.success("Offer fixed and the signing email went out again");
      setOfferEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOfferBusy(false); }
  };
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [batchDate, setBatchDate] = useState("");
  const [batchBusy, setBatchBusy] = useState(false);
  const [queue, setQueue] = useState([]);
  const [queueOpen, setQueueOpen] = useState(false);
  const [queueQuery, setQueueQuery] = useState("");
  const [assignTo, setAssignTo] = useState({});
  const [assignBusy, setAssignBusy] = useState("");
  const [scope, setScope] = useState("project");
  const [doneView, setDoneView] = useState("any");
  const [sortBy, setSortBy] = useState("hired_desc");
  const PROJECT_CUTOFF = "2024-12-01";
  const inProject = (e) => (e.hire_date && e.hire_date >= PROJECT_CUTOFF) || (e.last_visit && e.last_visit >= PROJECT_CUTOFF) || (e.first_visit && e.first_visit >= PROJECT_CUTOFF);
  const [queueLimit, setQueueLimit] = useState(100);
  const [archOpen, setArchOpen] = useState({});
  const [othersOpen, setOthersOpen] = useState({});
  const [printFrom, setPrintFrom] = useState("");
  const [printTo, setPrintTo] = useState("");
  const [archBusy, setArchBusy] = useState(false);
  const sheetInput = useRef(null);
  const [sheetBusy, setSheetBusy] = useState(false);
  const uploadSheet = async (ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = "";
    if (!f) return;
    setSheetBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/employees/hiring-register/data-entry-sheet/import", fd, { timeout: 300000 });
      toast.success(`Sheet applied — ${data.updated} updated, ${data.appended} added${data.skipped?.length ? `, ${data.skipped.length} skipped` : ""}`);
      if (data.skipped?.length) console.warn("sheet skipped rows:", data.skipped);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSheetBusy(false);
  };
  const { user } = useAuth();

  const archiveNoVisits = async () => {
    setArchBusy(true);
    try {
      const { data: prev } = await api.get("/employees/archive-no-visits/preview");
      if (!window.confirm(
        `Archive ${prev.archive_count} staff with NO shifts in the visit log?\n\nThey become Former/inactive (nothing is deleted).\nKept as new hires: ${prev.kept_new_hires.length ? prev.kept_new_hires.join(", ") : "none"}`)) { setArchBusy(false); return; }
      const { data } = await api.post("/employees/archive-no-visits", {});
      toast.success(`Archived ${data.archived} staff with no shifts — kept ${data.kept_new_hires.length} new hire(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setArchBusy(false);
  };

  const archiveItem = async (qid) => {
    try {
      await api.post(`/references/queue/${qid}/archive`, {});
      setQueue((q) => q.filter((x) => x.id !== qid));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteItem = async (qid, label) => {
    if (!window.confirm(`Delete "${label}" permanently? (Duplicate/junk only — archived items stay retrievable.)`)) return;
    try {
      await api.delete(`/references/queue/${qid}`);
      setQueue((q) => q.filter((x) => x.id !== qid));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const assign = async (qid) => {
    const uid = assignTo[qid];
    if (!uid) { toast.error("Pick an employee first"); return; }
    setAssignBusy(qid);
    try {
      await api.post(`/references/queue/${qid}/assign`, { uid });
      toast.success("Matched and filed");
      setQueue((q) => q.filter((x) => x.id !== qid));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setAssignBusy("");
  };

  const toggle = (uid) => setSelected((s) => { const n = new Set(s); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });

  const batchMark = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Mark 2 positive references for ${selected.size} employee(s), taken by you${batchDate ? `, dated ${batchDate}` : ", dated today"}?`)) return;
    setBatchBusy(true);
    try {
      const { data } = await api.post("/employees/hiring-record/batch-references", { uids: [...selected], date: batchDate || undefined });
      toast.success(`Marked positive references for ${data.updated} employee(s)`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBatchBusy(false);
  };

  const load = async () => {
    try {
      const { data } = await api.get("/employees/hiring-register");
      setRows(data.employees || []);
      setPipeline(data.pipeline_applicants || []);
      const d = {};
      for (const e of data.employees || []) {
        const refs = (e.references || []).map((r) => ({ ...r, verdict: reviewToVerdict(r.review) }));
        const posN = refs.filter((r) => !r.archived && r.verdict === "positive").length;
        while (refs.length < 2 && posN < 2) refs.push({ name: "", contact: "", type: "", known_for: "", q_kind: "", q_recommend: "", q_not_fit: "", verdict: "", reviewed_on: "", approved_by: user?.name || "" });
        d[e.uid] = { application_date: e.application_date, interview_date: e.interview_date, hire_date: e.hire_date, references: refs };
      }
      setDrafts(d);
      const { data: q } = await api.get("/references/queue");
      setQueue(q.items || []);
    } catch (e) { toast.error(formatApiError(e)); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const setDraft = (uid, patch) => setDrafts((d) => ({ ...d, [uid]: { ...d[uid], ...patch } }));

  const save = async (uid) => {
    setSaving(uid);
    try {
      await api.patch(`/employees/${uid}/hiring-record`, drafts[uid]);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving("");
  };

  const scoped = (rows || []).filter((e) => scope === "everyone" || inProject(e));
  const visible = scoped.filter((e) => !query || (e.name || "").toLowerCase().includes(query.toLowerCase()));
  const satCount = (draft) => (draft?.references || []).filter((r) => !r.archived && (r.verdict ?? reviewToVerdict(r.review)) === "positive").length;
  const rowComplete = (e) => {
    const d = drafts[e.uid];
    // all hiring requirements: 2 positive refs + the three dates + start date on/after hire date
    const timingBad = e.start_timing === "before_hire";
    return Boolean(satCount(d) >= 2 && d?.application_date && d?.interview_date && d?.hire_date && !timingBad);
  };
  const refComplete = (r) => Boolean(r?.name && (r.verdict ?? reviewToVerdict(r.review)) && r.reviewed_on && r.q_kind && r.q_recommend);
  const needsEntry = (e) => {
    if (!e.hire_date || e.hire_date < "2024-09-15") return false;
    const d = drafts[e.uid];
    const full = (d?.references || []).filter((r) => !r.archived && refComplete(r) && (r.verdict ?? reviewToVerdict(r.review)) === "positive").length;
    return full < 2;
  };
  const base = doneView === "entry"
    ? (rows || []).filter((e) => !query || (e.name || "").toLowerCase().includes(query.toLowerCase()))
    : visible;
  const filtered = base.filter((e) => {
    if (doneView === "any") return true;
    if (doneView === "entry") return needsEntry(e);
    return (doneView === "complete") === rowComplete(e);
  });  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
    const da = drafts[a.uid]?.hire_date || "";
    const dbt = drafts[b.uid]?.hire_date || "";
    return sortBy === "hired_asc" ? da.localeCompare(dbt) : dbt.localeCompare(da);
  });
  const completeCount = visible.filter(rowComplete).length;

  const printRegister = () => {
    const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const esc = (x) => String(x ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
    const refCard = (r) => {
      const v = r.verdict ?? reviewToVerdict(r.review);
      if (!r.name && !v) return "";
      const badge = v === "positive" ? '<span class="b ok">POSITIVE REFERENCE</span>' : v === "negative" ? '<span class="b bad">NOT POSITIVE</span>' : v === "no_response" ? '<span class="b">no response</span>' : '<span class="b">pending</span>';
      const qs = [
        r.q_kind && `kind &amp; good character: <b>${esc(r.q_kind)}</b>`,
        r.q_recommend && `recommends for vulnerable-care role: <b>${esc(r.q_recommend)}</b>`,
        r.reference_says_positive && `asked directly, says positive: <b>${esc(r.reference_says_positive)}</b>`,
        r.q_not_fit && `concern noted: ${esc(r.q_not_fit)}`,
      ].filter(Boolean).join(" · ");
      return `<div class="ref">
        <div class="refhead"><b>${esc(r.name || "(unnamed reference)")}</b> ${badge}${r.locked ? ` <span class="b re">email form — locked${r.submission_date ? ` · responded ${fmtDate(r.submission_date)}` : ""}</span>` : ""}</div>
        <div class="refmeta">${[r.type && esc(r.type), r.contact && esc(r.contact), r.known_for && `known ${esc(r.known_for)}`].filter(Boolean).join(" · ")}</div>
        ${qs ? `<div class="refmeta">${qs}</div>` : ""}
        ${r.note ? `<div class="refmeta">Work habits: ${esc(r.note)}</div>` : ""}
        ${r.verbatim ? `<div class="refmeta"><i>${esc(r.verbatim)}</i></div>` : ""}
        ${r.office_note ? `<div class="refmeta">Interviewer's impression: ${esc((r.office_note || "").replace(/^Interviewer's impression:\s*/i, ""))}</div>` : ""}
        <div class="refmeta dim">${[r.reviewed_on && `checked ${fmtDate(r.reviewed_on)}`, (r.reviewed_by || r.entered_by) && !r.locked && `taken by ${esc(r.reviewed_by || r.entered_by)}`, r.locked && "responded by email form", r.approved_by && `approved by ${esc(r.approved_by)}`].filter(Boolean).join(" · ")}</div>
      </div>`;
    };
    const empBlock = (e, i) => {
      const d = drafts[e.uid] || { references: [] };
      const activeAll = d.references.filter((r) => !r.archived);
      const complete = activeAll.filter((r) => refComplete(r) && (r.verdict ?? reviewToVerdict(r.review)) === "positive");
      // owner rule 2026-09-08: once 2 complete references are on file, print exactly those two;
      // partials/empties print only while references are still needed
      const active = complete.length >= 2 ? complete.slice(0, 2) : activeAll;
      const pendingHidden = activeAll.length - active.length;
      const arch = d.references.filter((r) => r.archived);
      const done = rowComplete(e);
      const timing = e.start_timing === "ok" ? '<span class="b ok">first visit on/after hire ✓</span>'
        : e.start_timing === "before_hire" ? `<span class="b bad">worked ${e.days_before_hire} days BEFORE hire date</span>`
        : e.start_timing === "possible_rehire" ? `<span class="b re">possible rehire — visit ${Math.round(e.days_before_hire / 30)} months before hire date</span>` : "";
      return `<div class="emp">
        <div class="emphead"><span class="num">${i + 1}</span><h2>${esc(e.name)}</h2>
          ${(e.phone || e.email) ? `<span class="dim">${esc([e.phone, e.email].filter(Boolean).join(" · "))}</span>` : ""}
          <span class="b ${done ? "ok" : "bad"}">${done ? "COMPLETE" : "NOT COMPLETE"}</span></div>
        <div class="dates">
          <div><span>Applied</span><b>${fmtDate(d.application_date) || "—"}</b></div>
          <div><span>Interviewed</span><b>${fmtDate(d.interview_date) || "—"}</b></div>
          <div><span>Hired</span><b>${fmtDate(d.hire_date) || "—"}</b></div>
          <div><span>First visit (log)</span><b>${fmtDate(e.first_visit) || "—"}</b>${timing}</div>
        </div>
        <div class="sect">References &amp; responses — ${satCount(d)} positive on record</div>
        ${active.map(refCard).filter(Boolean).join("") || '<p class="dim none">No references recorded.</p>'}
        ${pendingHidden ? `<p class="dim none">+ ${pendingHidden} pending reference(s) not shown — two positive on file, requirement met.</p>` : ""}
        ${arch.length ? `<p class="dim none">+ ${arch.length} archived extra reference(s) beyond the required two (on file).</p>` : ""}
        ${(e.reference_docs || []).length ? `<div class="sect">Reference documents on file</div><ul class="docs">${e.reference_docs.map((doc) => `<li>${esc(doc.title)}${doc.doc_date ? ` <span class="dim">— ${esc(doc.doc_date)}</span>` : ""}</li>`).join("")}</ul>` : ""}
      </div>`;
    };
    const printRows = sorted
      .filter((e) => {
        const h = drafts[e.uid]?.hire_date || "";
        if (printFrom && (!h || h < printFrom)) return false;
        if (printTo && (!h || h > printTo)) return false;
        return true;
      });
    const body = printRows.map(empBlock).join("");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>Silver Home Care — Hiring register</title><style>
      body{font-family:Georgia,'Times New Roman',serif;color:#1c1917;margin:36px;max-width:820px}
      .brand{display:flex;align-items:baseline;justify-content:space-between;border-bottom:3px double #6a3e97;padding-bottom:10px}
      h1{font-size:22px;margin:0;letter-spacing:.02em}
      .sub{color:#78716c;font-size:11px;margin-top:4px}
      .stats{display:flex;gap:22px;margin:14px 0 6px;font-size:11px;color:#44403c}
      .stats b{color:#1c1917}
      .emp{border:1px solid #d6d3d1;border-radius:8px;padding:14px 18px;margin:14px 0;page-break-inside:avoid}
      .emphead{display:flex;align-items:center;gap:10px}
      .emphead h2{font-size:15px;margin:0}
      .num{display:inline-block;width:20px;height:20px;line-height:20px;text-align:center;border-radius:50%;background:#6a3e97;color:#fff;font-size:10px}
      .b{display:inline-block;border-radius:999px;padding:1px 8px;font-size:9px;font-weight:bold;letter-spacing:.04em;background:#f5f5f4;color:#57534e}
      .b.ok{background:#e9f7ef;color:#1e8449}.b.bad{background:#fdedec;color:#C0392B}.b.re{background:#f3eefa;color:#6a3e97}
      .dates{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin:10px 0;padding:8px 0;border-top:1px solid #e7e5e4;border-bottom:1px solid #e7e5e4}
      .dates span{display:block;font-size:9px;text-transform:uppercase;letter-spacing:.05em;color:#a8a29e}
      .dates b{font-size:12px}
      .sect{font-size:10px;font-weight:bold;text-transform:uppercase;letter-spacing:.06em;color:#6a3e97;margin:10px 0 4px}
      .ref{border-left:3px solid #6a3e97;padding:4px 0 4px 10px;margin:6px 0}
      .refhead{font-size:12px}
      .refmeta{font-size:10.5px;color:#44403c;margin-top:1px}
      .dim{color:#a8a29e}.none{font-style:italic}
      ul.docs{margin:2px 0;padding-left:16px;font-size:10.5px}
      @media print{body{margin:14mm}}
      </style></head><body>
      <div class="brand"><div><h1>Silver Home Care</h1><div class="sub">Hiring register — references, key dates &amp; start-date cross-check</div></div>
      <div class="sub">Printed ${today}${printFrom || printTo ? ` · start dates ${esc(printFrom || "…")} – ${esc(printTo || "…")}` : ""}</div></div>
      <div class="stats"><span><b>${printRows.length}</b> employees shown</span><span><b>${printRows.filter(rowComplete).length}</b> complete</span><span><b>${printRows.length - printRows.filter(rowComplete).length}</b> pending</span><span>scope: <b>${scope === "project" ? "worked Dec 2024 – now" : "everyone"}</b></span>${doneView !== "any" ? `<span>view: <b>${doneView === "complete" ? "complete only" : "pending only"}</b></span>` : ""}</div>
      ${body}
      <p class="sub" style="margin-top:18px;border-top:1px solid #e7e5e4;padding-top:8px">This register is a snapshot — the app is the live record.</p>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="max-w-7xl" data-testid="hiring-register-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-stone-900">Hiring register</h1>
          <p className="mt-1 text-sm text-stone-500">
            Every employee's key hiring dates and references — blank fields are ready to type into. Two satisfactory references satisfy the roster rule. Press <b>Enter</b> in any field to save that row. Project scope = hired or worked Dec 2024 → now.
          </p>
          {(() => {
            let responses = 0, positive = 0, awaiting = 0, listed = 0, done2 = 0;
            for (const e of rows || []) {
              let pos = 0;
              for (const r of e.references || []) {
                if (r.archived) continue;
                if ((r.source || "") === "employee application") { listed++; continue; }
                responses++;
                if ((r.review || "").toLowerCase() === "satisfactory") { positive++; pos++; }
                if (r.locked && !(r.review || "")) awaiting++;
              }
              if (pos >= 2) done2++;
            }
            const Chip = ({ t, s, tone }) => (
              <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${tone}`}>{t}</span>
            );
            return (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="register-ref-totals">
                <Chip tone="border-stone-300 bg-white text-stone-600" t={`${responses} reference responses on file`} />
                <Chip tone="border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" t={`${positive} positive`} />
                {awaiting > 0 && <Chip tone="border-[#6a3e97]/30 bg-[#6a3e97]/10 text-[#6a3e97]" t={`${awaiting} emailed awaiting approval`} />}
                <Chip tone="border-stone-300 bg-stone-50 text-stone-500" t={`${listed} listed by employees on applications`} />
                <Chip tone="border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" t={`${done2} people reference-complete (2+ positive)`} />
              </div>
            );
          })()}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-full border border-stone-300 bg-white p-0.5" data-testid="register-scope-toggle">
            {[["project", `Project — worked Dec 2024+ (${(rows || []).filter(inProject).length})`], ["everyone", `Everyone (${(rows || []).length})`]].map(([v, label]) => (
              <button key={v} onClick={() => setScope(v)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${scope === v ? "bg-primary text-white" : "text-stone-500 hover:text-stone-800"}`}
                data-testid={`register-scope-${v}`}>{label}</button>
            ))}
          </div>
          <div className="inline-flex rounded-full border border-stone-300 bg-white p-0.5" data-testid="register-view-toggle">
            {[["any", `All (${visible.length})`], ["complete", `Complete (${completeCount})`], ["pending", `Pending (${visible.length - completeCount})`], ["entry", `Needs entry (${(rows || []).filter(needsEntry).length})`]].map(([v, label]) => (
              <button key={v} onClick={() => setDoneView(v)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${doneView === v ? "bg-primary text-white" : "text-stone-500 hover:text-stone-800"}`}
                data-testid={`register-view-${v}`}>{label}</button>
            ))}
          </div>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="h-9 rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid="register-sort">
            <option value="name">Sort: name</option>
            <option value="hired_desc">Sort: start date (newest)</option>
            <option value="hired_asc">Sort: start date (oldest)</option>
          </select>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search employee…"
            className="h-9 min-w-56 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="register-search" />
          <span className="flex items-center gap-1 text-[11px] text-stone-500">
            Start date
            <input type="date" value={printFrom} onChange={(e) => setPrintFrom(e.target.value)} className={`${inputCls} w-32`} data-testid="print-from" />
            –
            <input type="date" value={printTo} onChange={(e) => setPrintTo(e.target.value)} className={`${inputCls} w-32`} data-testid="print-to" />
          </span>
          <Button onClick={printRegister} variant="outline" className="h-9 gap-1.5 text-xs" data-testid="register-print">
            Print register
          </Button>
          <a href={`${api.defaults.baseURL}/employees/hiring-register/data-entry-sheet`}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 text-xs font-medium text-stone-700 hover:bg-stone-50"
            data-testid="register-data-entry-sheet" title="Excel: only the reference slots that still need entry, for staff hired since 09/15/2024 — fill in bulk">
            Data-entry sheet (xlsx)
          </a>
          <input ref={sheetInput} type="file" accept=".xlsx" className="hidden" data-testid="register-sheet-input" onChange={uploadSheet} />
          <Button onClick={() => sheetInput.current?.click()} disabled={sheetBusy} variant="outline" className="h-9 gap-1.5 text-xs"
            data-testid="register-sheet-upload" title="Upload the filled-in data-entry sheet — rows are matched by employee + slot and applied to the register">
            {sheetBusy ? "Importing…" : "Upload filled sheet"}
          </Button>
          <Button onClick={archiveNoVisits} disabled={archBusy} variant="outline" className="h-9 gap-1.5 text-xs text-[#8a5d14]" data-testid="archive-no-visits-btn">
            {archBusy ? "Checking…" : "Archive staff with no shifts"}
          </Button>
        </div>
      </div>

      {pipeline.length > 0 && (
        <section className="mt-4 rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="pipeline-applicants-card">
          <h2 className="font-heading text-sm font-semibold text-stone-900">Applicants in the pipeline</h2>
          <p className="mt-0.5 text-xs text-stone-500">Not on the employee register until they sign their offer. If a mistake was made on an offer, fix it and resend from here.</p>
          <div className="mt-3 space-y-2">
            {pipeline.map((p) => (
              <div key={p.id} className="rounded-lg border border-stone-100 bg-stone-50/60 p-3" data-testid={`pipeline-row-${p.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm">
                    <span className="font-medium text-stone-900">{p.name}</span>
                    <span className="ml-2 text-xs text-stone-400">{p.email}</span>
                    <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">{(p.status || "").replace(/_/g, " ")}</span>
                    {p.references_completed > 0 && <span className="ml-2 text-[11px] text-stone-500">refs {p.references_completed}/{p.references_listed}</span>}
                  </div>
                  {p.offer && !p.offer.signed_at && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-stone-500">
                        offer: {p.offer.position || "Direct Care Worker"} · ${p.offer.pay_rate}/hr
                        {p.offer.evv_bonus ? ` · EVV +$${p.offer.evv_bonus}` : ""}
                        {p.offer.differential ? ` · diff +$${p.offer.differential}` : ""}
                        {p.offer.start_date ? ` · starts ${p.offer.start_date}` : ""}
                      </span>
                      <a href={`/offer/${p.offer_token}`} target="_blank" rel="noreferrer"
                        className="rounded-full border border-stone-300 px-2.5 py-1 text-[11px] font-medium text-stone-600 hover:border-primary hover:text-primary"
                        data-testid={`pipeline-view-offer-${p.id}`}>View offer</a>
                      <button onClick={() => setOfferEdit({ aid: p.id, name: p.name, email: p.email, position: p.offer.position || "Direct Care Worker", pay_rate: p.offer.pay_rate || "", evv_bonus: p.offer.evv_bonus || "", differential: p.offer.differential || "", start_date: p.offer.start_date || "", schedule_notes: p.offer.schedule_notes || "" })}
                        className="rounded-full border border-stone-300 px-2.5 py-1 text-[11px] font-medium text-stone-600 hover:border-primary hover:text-primary"
                        data-testid={`pipeline-edit-offer-${p.id}`}>Fix offer</button>
                      <button onClick={() => resendOffer(p.id, p.name)} disabled={offerBusy}
                        className="rounded-full border border-primary/40 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary hover:bg-primary/20"
                        data-testid={`pipeline-resend-offer-${p.id}`}>Resend offer email</button>
                    </div>
                  )}
                  {p.offer && p.offer.signed_at && <span className="text-xs font-semibold text-[#1e8449]">offer signed ✓</span>}
                </div>
                {offerEdit && offerEdit.aid === p.id && (
                  <div className="mt-3 rounded-lg border border-primary/20 bg-white p-3" data-testid="pipeline-offer-editor">
                    <div className="grid gap-2 sm:grid-cols-3">
                      {[["position", "Position"], ["pay_rate", "Pay rate ($/hr)"], ["evv_bonus", "EVV bonus ($/hr)"],
                        ["differential", "Differential ($/hr)"], ["start_date", "Start date (YYYY-MM-DD)"], ["schedule_notes", "Schedule notes"]].map(([k, label]) => (
                        <div key={k}>
                          <label className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-stone-400">{label}</label>
                          <input value={offerEdit[k]} onChange={(e) => setOfferEdit((o) => ({ ...o, [k]: e.target.value }))}
                            className="w-full rounded-md border border-stone-300 px-2 py-1 text-sm" data-testid={`pipeline-offer-${k}`} />
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex gap-2">
                      <button onClick={() => setOfferEdit(null)} className="rounded-full border border-stone-300 px-3 py-1 text-xs text-stone-500" data-testid="pipeline-offer-cancel">Cancel</button>
                      <button onClick={saveOffer} disabled={offerBusy}
                        className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-white" data-testid="pipeline-offer-save">
                        {offerBusy ? "Saving…" : "Save & resend offer email"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {queue.length > 0 && (
        <div className="mt-4 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5" data-testid="ref-queue-panel">
          <button onClick={() => setQueueOpen((v) => !v)}
            className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-[#8a5d14]"
            data-testid="ref-queue-toggle">
            <span>Needs matching — {queue.length} reference item{queue.length === 1 ? "" : "s"} couldn't be matched to an employee automatically</span>
            <span className="text-xs">{queueOpen ? "▲ hide" : "▼ show"}</span>
          </button>
          {queueOpen && (
            <div className="border-t border-[#B7791F]/20 px-4 py-3">
              <input value={queueQuery} onChange={(e) => setQueueQuery(e.target.value)} placeholder="Search unmatched names…"
                className="mb-2 h-9 w-full max-w-sm rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="ref-queue-search" />
              <div className="max-h-96 space-y-1.5 overflow-y-auto">
                {(() => { const qf = queue.filter((it) => !queueQuery || it.parsed_name.toLowerCase().includes(queueQuery.toLowerCase())); return qf.slice(0, queueLimit).map((it) => (
                  <div key={it.id} className="flex flex-wrap items-center gap-2 rounded-md border border-stone-200 bg-white px-3 py-2" data-testid={`ref-queue-item-${it.id}`}>
                    <span className="min-w-44 text-sm font-medium text-stone-800">{it.parsed_name}</span>
                    <span className="text-xs text-stone-500">{it.detail}</span>
                    <span className="text-[10px] italic text-stone-400">{it.source_desc}</span>
                    <select value={assignTo[it.id] || ""} onChange={(e) => setAssignTo((m) => ({ ...m, [it.id]: e.target.value }))}
                      className="ml-auto h-8 max-w-48 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`ref-queue-select-${it.id}`}>
                      <option value="">— match to employee —</option>
                      {(rows || []).map((e) => <option key={e.uid} value={e.uid}>{e.name}</option>)}
                    </select>
                    <Button onClick={() => assign(it.id)} disabled={assignBusy === it.id} className="h-8 text-xs" data-testid={`ref-queue-assign-${it.id}`}>
                      {assignBusy === it.id ? "Filing…" : "Match"}
                    </Button>
                    <button onClick={() => archiveItem(it.id)} className="text-[10px] font-semibold text-stone-400 hover:text-stone-700 hover:underline" data-testid={`ref-queue-archive-${it.id}`}>Archive</button>
                    <button onClick={() => deleteItem(it.id, `${it.parsed_name} — ${it.detail}`)} className="text-[10px] font-semibold text-[#C0392B]/70 hover:text-[#C0392B] hover:underline" data-testid={`ref-queue-delete-${it.id}`}>Delete</button>
                  </div>
                )); })()}
                {(() => { const n = queue.filter((it) => !queueQuery || it.parsed_name.toLowerCase().includes(queueQuery.toLowerCase())).length; return n > queueLimit && (
                  <button onClick={() => setQueueLimit((l) => l + 200)}
                    className="w-full rounded-md border border-dashed border-stone-300 py-2 text-xs font-semibold text-stone-500 hover:border-primary hover:text-primary"
                    data-testid="ref-queue-more">Show more ({(n - queueLimit).toLocaleString()} remaining)</button>
                ); })()}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-5 overflow-x-auto rounded-lg border border-stone-200 bg-white shadow-sm">
        <table className="w-full table-fixed text-left text-sm">
          <thead className="border-b border-stone-200 bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
            <tr>
              <th className="w-8 px-2 py-3">
                <input type="checkbox" aria-label="Select all"
                  checked={visible.length > 0 && visible.every((e) => selected.has(e.uid))}
                  onChange={(e) => setSelected(e.target.checked ? new Set(visible.map((x) => x.uid)) : new Set())}
                  data-testid="register-select-all" />
              </th>
              <th className="px-4 py-3">Employee</th>
              <th className="w-32 px-4 py-3">Applied</th>
              <th className="w-32 px-4 py-3">Interviewed</th>
              <th className="w-32 px-4 py-3">Hired</th>
              <th className="w-20 px-4 py-3">First visit</th>
              <th className="w-[42%] px-4 py-3">References &amp; responses</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((e) => {
              const d = drafts[e.uid] || { references: [] };
              const sat = satCount(d);
              return (
                <tr key={e.uid} className="border-b border-stone-100 align-top last:border-0 hover:bg-accent/20" data-testid={`register-row-${e.uid}`}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter" && ["INPUT", "SELECT"].includes(ev.target.tagName)) { ev.preventDefault(); save(e.uid); }
                  }}>
                  <td className="px-2 py-3">
                    <input type="checkbox" aria-label={`Select ${e.name}`} checked={selected.has(e.uid)} onChange={() => toggle(e.uid)}
                      data-testid={`register-select-${e.uid}`} />
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/team/${e.uid}`}
                      className={`font-medium hover:underline ${rowComplete(e) ? "font-bold text-[#1e8449]" : "text-primary"}`}
                      title={rowComplete(e) ? "All hiring requirements met" : "Still has open hiring items"}
                      data-testid={`register-employee-${e.uid}`}>{e.name}</Link>
                    {(e.phone || e.email) && (
                      <span className="block text-[11px] font-medium text-stone-600" data-testid={`register-contact-${e.uid}`}>
                        {[e.phone, e.email].filter(Boolean).join(" · ")}
                      </span>
                    )}
                    <span className="block text-[10px] text-stone-400">{e.active ? "Active" : "Inactive"} · {e.role}</span>
                    <span className={`mt-1 inline-block rounded-full border px-2 py-px text-[9px] font-bold ${sat >= 2 ? "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]" : "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]"}`}
                      data-testid={`register-sat-${e.uid}`}>{sat}/2 satisfactory</span>
                    {(() => {
                      const pos = (d.references || []).filter((r) => !r.archived && (r.verdict ?? reviewToVerdict(r.review)) === "positive");
                      if (pos.length < 2 || !d.hire_date) return null;
                      const dated = pos.filter((r) => r.reviewed_on);
                      if (dated.length < 2) return <span className="mt-0.5 block w-fit rounded-full bg-stone-100 px-2 py-px text-[9px] font-bold text-stone-500" title="Positive references are missing their checked dates" data-testid={`register-ref-timing-${e.uid}`}>ref dates needed</span>;
                      const late = dated.filter((r) => r.reviewed_on > d.hire_date);
                      return late.length === 0
                        ? <span className="mt-0.5 block w-fit whitespace-nowrap rounded-full bg-[#27AE60]/10 px-2 py-px text-[9px] font-bold text-[#1e8449]" title="Both positive references were checked before the hire date" data-testid={`register-ref-timing-${e.uid}`}>✓ refs before start</span>
                        : <span className="mt-0.5 block w-fit whitespace-nowrap rounded-full bg-[#C0392B]/10 px-2 py-px text-[9px] font-bold text-[#C0392B]" title={`${late.length} reference(s) dated after the hire date — references must be checked before starting`} data-testid={`register-ref-timing-${e.uid}`}>✗ {late.length} ref after start</span>;
                    })()}
                  </td>
                  {["application_date", "interview_date", "hire_date"].map((k) => (
                    <td key={k} className="px-4 py-3">
                      <input type="date" value={d[k] || ""} onChange={(ev) => setDraft(e.uid, { [k]: ev.target.value })}
                        className={inputCls} data-testid={`register-${k.replace("_", "-")}-${e.uid}`} />
                    </td>
                  ))}
                  <td className="px-4 py-3" data-testid={`register-first-visit-${e.uid}`}>
                    {e.first_visit ? (
                      <div>
                        <span className="text-xs font-medium text-stone-700">{fmtDate(e.first_visit)}</span>
                        {!d.hire_date && <span title="No hire date is entered, so the first visit can't be checked against it" className="mt-0.5 block w-fit whitespace-nowrap rounded-full bg-stone-100 px-2 py-px text-[9px] font-bold text-stone-500">need hire date to check</span>}
                        {e.start_timing === "ok" && <span title="Their first visit in the visit log is on or after their hire date — they started when the record says they did" className="mt-0.5 block w-fit whitespace-nowrap rounded-full bg-[#27AE60]/10 px-2 py-px text-[9px] font-bold text-[#1e8449]">✓ started on/after hire</span>}
                        {e.start_timing === "before_hire" && <span title={`First visit was ${e.days_before_hire} days BEFORE the hire date — the hire date looks wrong`} className="mt-0.5 block w-fit whitespace-nowrap rounded-full bg-[#C0392B]/10 px-2 py-px text-[9px] font-bold text-[#C0392B]">✗ worked {e.days_before_hire}d before hire</span>}
                        {e.start_timing === "possible_rehire" && <span title={`First visit was ${Math.round(e.days_before_hire / 30)} months before the hire date — probably an earlier stint, so this is likely a rehire`} className="mt-0.5 block w-fit whitespace-nowrap rounded-full bg-[#6a3e97]/10 px-2 py-px text-[9px] font-bold text-[#6a3e97]">rehire? visit {Math.round(e.days_before_hire / 30)} mo earlier</span>}
                      </div>
                    ) : <span className="text-xs text-stone-300">no shifts in visit log</span>}
                  </td>
                  <td className="px-4 py-3 align-top">
                    {(() => {
                      const items = d.references.map((r, i) => ({ r, i })).filter((x) => !x.r.archived);
                      const posN = items.filter((x) => (x.r.verdict ?? reviewToVerdict(x.r.review)) === "positive").length;
                      const reqMet = posN >= 2;
                      // Group by reference PERSON: the application listing ("given") on top,
                      // actual responses (verbal call / tracker / email form) below.
                      const normName = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
                      const groups = [];
                      const gmap = new Map();
                      for (const x of items) {
                        const k = normName(x.r.name) || `__row${x.i}`;
                        if (!gmap.has(k)) { gmap.set(k, { key: k, given: null, rows: [] }); groups.push(gmap.get(k)); }
                        const g = gmap.get(k);
                        const hasOut = !!(x.r.verdict ?? reviewToVerdict(x.r.review)) || x.r.locked;
                        if ((x.r.source || "") === "employee application" && !hasOut) g.given = g.given || x;
                        g.rows.push(x);
                      }
                      const personStatus = (g) => {
                        const resp = g.rows.filter((x) => x !== g.given);
                        const done = resp.some((x) => (x.r.verdict ?? reviewToVerdict(x.r.review)) === "positive" && x.r.reviewed_on);
                        if (done) return ["COMPLETE", "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]"];
                        if (resp.some((x) => (x.r.verdict ?? reviewToVerdict(x.r.review)))) return ["OUTCOME SET — DATE NEEDED", "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]"];
                        if (resp.length) return ["RESPONSE ON FILE — NEEDS REVIEW", "border-[#6a3e97]/30 bg-[#6a3e97]/10 text-[#6a3e97]"];
                        return ["LISTED — CALL NOT MADE YET", "border-stone-300 bg-stone-100 text-stone-500"];
                      };
                      const groupPending = (g) => !g.rows.some((x) => (x.r.verdict ?? reviewToVerdict(x.r.review)) || x.r.locked);
                      // owner rule 2026-09-08: once 2 COMPLETE positive references are on file, show exactly
                      // those two — partials/empties only appear while references are still needed
                      const completeG = groups.filter((g) => g.rows.some((x) => refComplete(x.r) && (x.r.verdict ?? reviewToVerdict(x.r.review)) === "positive"));
                      const shownG = reqMet ? completeG.slice(0, 2) : groups;
                      const hiddenG = reqMet ? groups.filter((g) => !shownG.includes(g)) : [];
                      const slot = ({ r, i }, label) => (
                        <div key={i}>
                          {label && <p className="mb-0.5 text-[9px] font-bold uppercase tracking-wide text-stone-400">{label}</p>}
                          <RefSlot ref_={r} testid={`register-ref-${e.uid}-${i}`} refDocs={e.reference_docs || []} me={user?.name || user?.email || ""}
                            uid={e.uid} idx={i} isAdmin={user?.role === "admin"} onUnlock={load}
                            onChange={(nr) => {
                              const refs = [...d.references];
                              refs[i] = nr;
                              setDraft(e.uid, { references: refs });
                            }} />
                        </div>
                      );
                      const SRC_LABEL = { "verbal personal references form": "Response — verbal call (office)", "verbal professional references form": "Response — verbal call (office, professional)", "reference tracker import": "Response — office tracker", "online reference form": "Response — emailed form", "online reference form (professional)": "Response — emailed form (professional)", "airtable new-hire import": "Response — new-hire record", "office": "Response — office entry", "employee application": "Listed by employee — record the call here" };
                      return (
                        <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                          {shownG.map((g) => {
                            const [st, stCls] = personStatus(g);
                            const name = (g.given?.r.name || g.rows[0].r.name || "(unnamed reference)").trim();
                            return (
                              <div key={g.key} className={`rounded-lg border p-1.5 ${st === "COMPLETE" ? "border-[#27AE60]/40" : "border-stone-200"}`} data-testid={`register-person-${e.uid}-${g.key}`}>
                                <div className="mb-1 flex flex-wrap items-center gap-1.5 px-0.5">
                                  <span className="text-xs font-bold text-stone-800">{name}</span>
                                  <span className={`rounded-full border px-2 py-px text-[9px] font-bold ${stCls}`} data-testid={`register-person-status-${e.uid}-${g.key}`}>{st}</span>
                                </div>
                                {g.given && g.rows.length > 1 && (
                                  <p className="mb-1 rounded border border-stone-200 bg-stone-50 px-2 py-1 text-[10px] text-stone-500" data-testid={`register-person-given-${e.uid}-${g.key}`}>
                                    <b>Employee listed:</b> {[g.given.r.contact, g.given.r.type, g.given.r.known_for && `known ${g.given.r.known_for}`].filter(Boolean).join(" · ") || "no contact details"}
                                  </p>
                                )}
                                <div className="space-y-1.5">
                                  {(reqMet ? g.rows.filter((x) => refComplete(x.r)) : g.rows).map((x) => slot(x, g.rows.length > 1 ? (SRC_LABEL[x.r.source] || "Response") : null))}
                                </div>
                              </div>
                            );
                          })}
                          {hiddenG.length > 0 && (
                            <>
                              <button onClick={() => setOthersOpen((m) => ({ ...m, [e.uid]: !m[e.uid] }))}
                                className="col-span-full text-left text-[10px] italic text-stone-400 hover:text-stone-600 hover:underline"
                                data-testid={`register-other-refs-${e.uid}`}>
                                {othersOpen[e.uid] ? "▲ hide" : "▸ show"} {hiddenG.length} other listed reference{hiddenG.length === 1 ? "" : "s"} — requirement met (2 complete on file)
                              </button>
                              {othersOpen[e.uid] && hiddenG.map((g) => (
                                <div key={g.key} className="rounded-lg border border-stone-200 p-1.5">
                                  <p className="mb-1 px-0.5 text-xs font-bold text-stone-500">{(g.given?.r.name || g.rows[0].r.name || "(unnamed)").trim()}</p>
                                  <div className="space-y-1.5">{g.rows.map((x) => slot(x, null))}</div>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      );
                    })()}
                    {d.references.some((r) => r.archived) && (
                      <>
                        <button onClick={() => setArchOpen((m) => ({ ...m, [e.uid]: !m[e.uid] }))}
                          className="text-[10px] italic text-stone-400 hover:text-stone-600 hover:underline"
                          data-testid={`register-archived-refs-${e.uid}`}>
                          {archOpen[e.uid] ? "▲ hide" : "▸ show"} {d.references.filter((r) => r.archived).length} archived (extras beyond 2 positive — kept for the record)
                        </button>
                        {archOpen[e.uid] && d.references.map((r, i) => r.archived && (
                          <p key={i} className="rounded border border-stone-100 bg-stone-50 px-2 py-1 text-[10px] italic text-stone-400" data-testid={`register-arch-ref-${e.uid}-${i}`}>
                            {r.name || "(unnamed reference)"} — {r.review || "—"}{r.reviewed_on ? ` · ${r.reviewed_on}` : ""}{r.archived_reason ? ` · ${r.archived_reason}` : ""}
                          </p>
                        ))}
                      </>
                    )}
                    <button onClick={() => setDraft(e.uid, { references: [...d.references, { name: "", contact: "", type: "", known_for: "", q_kind: "", q_recommend: "", q_not_fit: "", verdict: "", reviewed_on: "", approved_by: user?.name || "" }] })}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline" data-testid={`register-add-ref-${e.uid}`}>
                      <Plus size={11} /> Add reference
                    </button>
                    {(e.reference_docs?.length > 0 || true) && (
                      <div className="mt-1 border-t border-stone-200 pt-1.5" data-testid={`register-ref-docs-${e.uid}`}>
                        {e.reference_docs?.length > 0 && (
                          <p className="mb-1 flex flex-wrap items-center gap-1">
                            <span className="text-[9px] font-bold uppercase text-stone-400">On file:</span>
                            {e.reference_docs.map((doc) => (
                              <a key={doc.id} href={`${api.defaults.baseURL}/employee-file-docs/${doc.id}/file`} target="_blank" rel="noreferrer"
                                className="max-w-44 truncate rounded-full border border-stone-200 bg-white px-2 py-px text-[10px] font-medium text-primary hover:bg-accent"
                                title={doc.title} data-testid={`register-ref-doc-${doc.id}`}>
                                {doc.title}{doc.doc_date ? ` · ${doc.doc_date}` : ""}
                              </a>
                            ))}
                          </p>
                        )}
                        <label className="inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold text-primary hover:underline" data-testid={`register-upload-ref-${e.uid}`}>
                          <Plus size={11} /> Upload completed reference
                          <input type="file" accept=".pdf,image/*" className="hidden"
                            onChange={async (ev) => {
                              const file = ev.target.files?.[0];
                              ev.target.value = "";
                              if (!file) return;
                              try {
                                const fd = new FormData();
                                fd.append("file", file);
                                await api.post(`/employees/${e.uid}/file-documents?section=hiring`, fd, { timeout: 240000 });
                                toast.success(`Filed under ${e.name} — Hiring process`);
                                load();
                              } catch (err) { toast.error(formatApiError(err)); }
                            }} />
                        </label>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button onClick={() => save(e.uid)} disabled={saving === e.uid}
                      className="h-8 gap-1 text-xs" data-testid={`register-save-${e.uid}`}>
                      <FloppyDisk size={13} /> {saving === e.uid ? "Saving…" : "Save"}
                    </Button>
                  </td>
                </tr>
              );
            })}
            {rows !== null && visible.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-stone-400" data-testid="register-empty">No employees match.</td></tr>
            )}
            {rows === null && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-stone-400">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected.size > 0 && (
        <div className="sticky bottom-4 mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-primary/30 bg-white p-4 shadow-lg" data-testid="register-batch-bar">
          <span className="text-sm font-semibold text-stone-800" data-testid="register-batch-count">{selected.size} selected</span>
          <label className="flex items-center gap-1.5 text-xs text-stone-500">
            Positive reference date
            <input type="date" value={batchDate} onChange={(e) => setBatchDate(e.target.value)} className={`${inputCls} w-36`} data-testid="register-batch-date" />
          </label>
          <Button onClick={batchMark} disabled={batchBusy} className="gap-1 text-xs" data-testid="register-batch-apply">
            {batchBusy ? "Marking…" : "Mark 2 positive references for selected"}
          </Button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-stone-400 hover:underline" data-testid="register-batch-clear">Clear selection</button>
        </div>
      )}
    </div>
  );
}
